import { useCallback, useEffect, useMemo, useState } from "react";

import { DEFAULT_BRANCH_CANDIDATES } from "./composerChips";

/**
 * Which branch a new thread starts from.
 *
 * Three rules that only make sense together:
 *
 * - the project's own default wins over `main`, but only until the user picks
 *   something, after which the app must stop overriding them;
 * - switching project clears that choice, because a branch named in one
 *   project usually does not exist in the next;
 * - and the candidate list always offers the defaults plus whatever the project
 *   actually reports, so a repository on `trunk` is not asked to type it.
 *
 * `branchUserSelected` is the whole point: without it the default-branch effect
 * would fire whenever git status resolved and quietly undo a deliberate choice.
 */
export function useDraftBranchSelection(input: {
  readonly projectKey: string | null | undefined;
  readonly projectDefaultBranch: string | null;
}): {
  readonly branch: string;
  readonly branchUserSelected: boolean;
  readonly knownBranches: readonly string[];
  readonly selectBranch: (next: string) => void;
} {
  const { projectKey, projectDefaultBranch } = input;
  const [branch, setBranch] = useState("main");
  const [branchUserSelected, setBranchUserSelected] = useState(false);

  useEffect(() => {
    setBranchUserSelected(false);
    setBranch("main");
  }, [projectKey]);

  useEffect(() => {
    if (branchUserSelected) return;
    setBranch(projectDefaultBranch ?? "main");
  }, [projectDefaultBranch, branchUserSelected]);

  const knownBranches = useMemo(() => {
    const branches = new Set<string>(DEFAULT_BRANCH_CANDIDATES);
    if (projectDefaultBranch) branches.add(projectDefaultBranch);
    return [...branches];
  }, [projectDefaultBranch]);

  const selectBranch = useCallback((next: string) => {
    setBranchUserSelected(true);
    setBranch(next);
  }, []);

  return { branch, branchUserSelected, knownBranches, selectBranch };
}
